/**
 * ReservationsPinLogin — PIN personale SARABI OS (stesso /auth/pin-login della Staff App).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Delete, Loader2 } from "lucide-react";

const KEYS = ["1","2","3","4","5","6","7","8","9","","0","<"];

export default function ReservationsPinLogin() {
  const nav = useNavigate();
  const { user, pinLogin } = useAuth();
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (user) { nav("/reservations", { replace: true }); } }, [user, nav]);

  const doLogin = useCallback(async (val) => {
    setBusy(true); setErr("");
    try {
      await pinLogin(val);
      setTimeout(() => nav("/reservations", { replace: true }), 60);
    } catch (e) {
      setShake(true);
      setErr(e?.response?.status === 429
             ? "Troppi tentativi. Attendi qualche minuto."
             : "PIN non riconosciuto");
      setTimeout(() => { setShake(false); setPin(""); setBusy(false); }, 380);
    }
  }, [pinLogin, nav]);

  const tap = useCallback((k) => {
    if (busy || !k) { return; }
    if (k === "<") { setPin((p) => p.slice(0, -1)); setErr(""); return; }
    setErr("");
    setPin((p) => {
      const next = (p + k).slice(0, 4);
      if (next.length === 4 && p.length < 4) { doLogin(next); }
      return next;
    });
  }, [busy, doLogin]);

  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) { tap(e.key); }
      else if (e.key === "Backspace") { tap("<"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tap]);

  return (
    <div className="min-h-[100dvh] bg-sand-100 flex items-center justify-center px-5
                    pt-[max(2rem,env(safe-area-inset-top))] pb-[max(2rem,env(safe-area-inset-bottom))]"
         data-testid="reservations-pin-page">
      <div className={`w-full max-w-[340px] ${shake ? "animate-shake" : ""}`}>
        <div className="text-center mb-8">
          <div className="eyebrow text-[10px]">Sarabi OS</div>
          <h1 className="font-serif text-4xl mt-1 text-ink-900">Reservations</h1>
          <p className="text-sm text-ink-500 mt-2">Accedi con il tuo PIN personale Staff</p>
        </div>

        <div className="flex justify-center gap-4 mb-6" data-testid="reservations-pin-dots">
          {[0,1,2,3].map((i) => (
            <div key={i}
                 className={`w-4 h-4 rounded-full border-2 transition-transform duration-150 ${
                   i < pin.length ? "bg-bronze-700 border-bronze-700 scale-110" : "border-sand-400"
                 }`}
                 data-testid={`reservations-pin-dot-${i}`} />
          ))}
        </div>

        <div className="h-6 mb-2 text-center text-sm" aria-live="polite">
          {busy && !err && <Loader2 className="w-4 h-4 animate-spin mx-auto text-bronze-700" />}
          {err && <span className="text-rose-700" data-testid="reservations-pin-err">{err}</span>}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {KEYS.map((k, i) => {
            if (!k) { return <div key={i} />; }
            const isDel = k === "<";
            return (
              <button key={i} type="button" disabled={busy} onClick={() => tap(k)}
                      data-testid={`reservations-pin-key-${k}`}
                      className={`h-[68px] rounded-2xl border-2 border-sand-300 bg-white font-serif text-3xl
                                  active:scale-95 transition-transform disabled:opacity-40
                                  ${isDel ? "text-rose-600" : "text-ink-900 hover:bg-sand-50"}`}>
                {isDel ? <Delete className="w-6 h-6 mx-auto" strokeWidth={1.5} /> : k}
              </button>
            );
          })}
        </div>
        <p className="text-center mt-8 text-[11px] text-ink-500">Stesso PIN di SARABI OS Staff</p>
      </div>
      <style>{`
        @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-8px)} 75%{transform:translateX(8px)} }
        .animate-shake { animation: shake .38s ease; }
      `}</style>
    </div>
  );
}
